import { useState } from 'react';
import axios from 'axios';
import { X, Plus, Edit2, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RawMaterialsManager = ({ password }) => {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState(['General']);
  const [loading, setLoading] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    type: 'quantity',
    options: '',
    price_per_unit: '',
    branch_price: '',
    unit: 'unit',
    category: 'General',
    is_kiosk: true,
    is_express: true,
    only_branch: false,
    active: true
  });

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/raw-materials-full`, {
        headers: { 'X-Admin-Password': password }
      });
      setMaterials(response.data);
    } catch (error) {
      toast.error('Failed to load raw materials');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/raw-materials-categories`, {
        headers: { 'X-Admin-Password': password }
      });
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to load categories', error);
      setCategories(['General']);
    }
  };

  useState(() => {
    fetchMaterials();
    fetchCategories();
  }, []);

  const handleAddNewCategory = () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter category name');
      return;
    }
    
    // Add to categories list if not already exists
    if (!categories.includes(newCategoryName.trim())) {
      const updatedCategories = [...categories, newCategoryName.trim()].sort();
      setCategories(updatedCategories);
    }
    
    // Select the new category
    setFormData({...formData, category: newCategoryName.trim()});
    setNewCategoryName('');
    setShowNewCategoryInput(false);
    toast.success('Category created! Selected for this material.');
  };

  const handleAdd = async () => {
    if (!formData.name || !formData.price_per_unit) {
      toast.error('Please fill required fields');
      return;
    }
    
    // Validate at least one franchise model is selected
    if (!formData.is_kiosk && !formData.is_express && !formData.only_branch) {
      toast.error('Please select at least one option (Kiosk, Express, or Branch)');
      return;
    }

    try {
      await axios.post(`${API}/admin/raw-materials`, formData, {
        headers: { 'X-Admin-Password': password }
      });
      toast.success('Material added successfully');
      setShowAddForm(false);
      setShowNewCategoryInput(false);
      setNewCategoryName('');
      setFormData({ 
        name: '', 
        type: 'quantity', 
        options: '', 
        price_per_unit: '', 
        branch_price: '',
        unit: 'unit', 
        category: 'General', 
        is_kiosk: true,
        is_express: true,
        only_branch: false,
        active: true 
      });
      fetchMaterials();
      fetchCategories();
    } catch (error) {
      toast.error('Failed to add material');
      console.error(error);
    }
  };

  const handleUpdate = async (rowNumber, data) => {
    try {
      await axios.put(`${API}/admin/raw-materials/${rowNumber}`, data, {
        headers: { 'X-Admin-Password': password }
      });
      toast.success('Material updated successfully');
      setEditingRow(null);
      fetchMaterials();
    } catch (error) {
      toast.error('Failed to update material');
      console.error(error);
    }
  };

  const handleDelete = async (rowNumber, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;

    try {
      await axios.delete(`${API}/admin/raw-materials/${rowNumber}`, {
        headers: { 'X-Admin-Password': password }
      });
      toast.success('Material deleted successfully');
      fetchMaterials();
    } catch (error) {
      toast.error('Failed to delete material');
      console.error(error);
    }
  };

  if (loading) return <div style={styles.loading}>Loading materials...</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Raw Materials Management</h2>
        <button
          data-testid="add-material-btn"
          onClick={() => setShowAddForm(true)}
          style={styles.addButton}
        >
          <Plus size={18} />
          Add Material
        </button>
      </div>

      {showAddForm && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h3>Add New Raw Material</h3>
              <button onClick={() => setShowAddForm(false)} style={styles.closeBtn}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.form}>
              <div style={styles.formGroup}>
                <label className="label">Item Name *</label>
                <input
                  className="input-field"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Coffee Powder"
                />
              </div>

              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label className="label">Type</label>
                  <select
                    className="select-field"
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                  >
                    <option value="quantity">Quantity</option>
                    <option value="sized">Sized</option>
                  </select>
                </div>

                <div style={styles.formGroup}>
                  <label className="label">Price per Unit *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.price_per_unit}
                    onChange={(e) => setFormData({...formData, price_per_unit: e.target.value})}
                    placeholder="e.g., 530 or 530,750,1200.50"
                  />
                  <small style={styles.hint}>For multiple prices (variants), separate with commas</small>
                </div>

                <div style={styles.formGroup}>
                  <label className="label">Unit</label>
                  <input
                    className="input-field"
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    placeholder="kg, piece, bundle"
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label className="label">Category *</label>
                {!showNewCategoryInput ? (
                  <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                    <select
                      className="select-field"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      style={{flex: 1}}
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowNewCategoryInput(true)}
                      style={styles.addCategoryBtn}
                      title="Add New Category"
                    >
                      <Plus size={16} /> New
                    </button>
                  </div>
                ) : (
                  <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                    <input
                      className="input-field"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="Enter new category name"
                      style={{flex: 1}}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={handleAddNewCategory}
                      style={styles.saveNewCategoryBtn}
                      title="Create Category"
                    >
                      <Save size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategoryName('');
                      }}
                      style={styles.cancelNewCategoryBtn}
                      title="Cancel"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label className="label">Branch Price (Optional)</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.branch_price}
                  onChange={(e) => setFormData({...formData, branch_price: e.target.value})}
                  placeholder="e.g., 480 or 480,690,1100 (for Branch outlet)"
                />
                <small style={styles.hint}>Special pricing for Branch outlet. Leave empty to use regular price.</small>
              </div>

              <div style={styles.formGroup}>
                <label className="label">Options (comma or pipe separated)</label>
                <input
                  className="input-field"
                  value={formData.options}
                  onChange={(e) => setFormData({...formData, options: e.target.value})}
                  placeholder="e.g., 5kg,7.5kg,10kg or 0|1|2|3"
                />
                <small style={styles.hint}>Use pipe (|) for numeric sequences to avoid conversion issues</small>
              </div>

              <div style={styles.formGroup}>
                <label className="label">Available For (Select at least one) *</label>
                <div style={{display: 'flex', gap: '20px', marginTop: '8px'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                    <input
                      type="checkbox"
                      checked={formData.is_kiosk}
                      onChange={(e) => setFormData({...formData, is_kiosk: e.target.checked})}
                      style={{width: '18px', height: '18px', cursor: 'pointer'}}
                    />
                    <span>Kiosk Franchises</span>
                  </label>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                    <input
                      type="checkbox"
                      checked={formData.is_express}
                      onChange={(e) => setFormData({...formData, is_express: e.target.checked})}
                      style={{width: '18px', height: '18px', cursor: 'pointer'}}
                    />
                    <span>Express Franchises</span>
                  </label>
                  <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'}}>
                    <input
                      type="checkbox"
                      checked={formData.only_branch}
                      onChange={(e) => setFormData({...formData, only_branch: e.target.checked})}
                      style={{width: '18px', height: '18px', cursor: 'pointer'}}
                    />
                    <span>Branch Only</span>
                  </label>
                </div>
                <small style={styles.hint}>Select which franchise models can order this material. "Branch Only" = visible only to specific branch franchise</small>
              </div>

              <div style={styles.formActions}>
                <button onClick={() => setShowAddForm(false)} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={handleAdd} className="btn-primary">
                  <Save size={18} style={{marginRight: '8px'}} />
                  Add Material
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{...styles.th, width: '14%'}}>Item Name</th>
              <th style={{...styles.th, width: '7%'}}>Type</th>
              <th style={{...styles.th, width: '9%'}}>Category</th>
              <th style={{...styles.th, width: '11%'}}>Options</th>
              <th style={{...styles.th, width: '9%'}}>Price</th>
              <th style={{...styles.th, width: '9%'}}>Branch Price</th>
              <th style={{...styles.th, width: '6%'}}>Unit</th>
              <th style={{...styles.th, width: '7%'}}>Kiosk</th>
              <th style={{...styles.th, width: '7%'}}>Express</th>
              <th style={{...styles.th, width: '7%'}}>Branch</th>
              <th style={{...styles.th, width: '7%'}}>Status</th>
              <th style={{...styles.th, width: '7%'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {materials.map((material) => (
              <tr key={material.row_number} style={styles.tr}>
                {editingRow === material.row_number ? (
                  <EditRow
                    material={material}
                    categories={categories}
                    onSave={(data) => handleUpdate(material.row_number, data)}
                    onCancel={() => setEditingRow(null)}
                  />
                ) : (
                  <>
                    <td style={styles.td}>{material.name}</td>
                    <td style={styles.td}>{material.type}</td>
                    <td style={styles.td}>
                      <span style={styles.categoryBadge}>{material.category || 'General'}</span>
                    </td>
                    <td style={styles.td}>{material.options || '-'}</td>
                    <td style={styles.td}>Rs. {material.price_per_unit}</td>
                    <td style={styles.td}>{material.branch_price ? `Rs. ${material.branch_price}` : '-'}</td>
                    <td style={styles.td}>{material.unit}</td>
                    <td style={styles.td}>
                      <span style={material.is_kiosk ? styles.yesBadge : styles.noBadge}>
                        {material.is_kiosk ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={material.is_express ? styles.yesBadge : styles.noBadge}>
                        {material.is_express ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={material.only_branch ? styles.yesBadge : styles.noBadge}>
                        {material.only_branch ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={material.active ? styles.activeBadge : styles.inactiveBadge}>
                        {material.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          onClick={() => setEditingRow(material.row_number)}
                          style={styles.editBtn}
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(material.row_number, material.name)}
                          style={styles.deleteBtn}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {materials.length === 0 && (
          <div style={styles.emptyState}>
            <p>No raw materials found. Add your first material!</p>
          </div>
        )}
      </div>
    </div>
  );
};

const EditRow = ({ material, onSave, onCancel, categories }) => {
  const [data, setData] = useState({
    name: material.name,
    type: material.type,
    options: material.options,
    price_per_unit: material.price_per_unit,
    branch_price: material.branch_price || '',
    unit: material.unit,
    category: material.category || 'General',
    is_kiosk: material.is_kiosk !== undefined ? material.is_kiosk : true,
    is_express: material.is_express !== undefined ? material.is_express : true,
    only_branch: material.only_branch !== undefined ? material.only_branch : false,
    active: material.active
  });

  return (
    <>
      <td style={styles.td}>
        <input
          className="input-field"
          value={data.name}
          onChange={(e) => setData({...data, name: e.target.value})}
          style={{fontSize: '13px', padding: '4px', width: '100%'}}
        />
      </td>
      <td style={styles.td}>
        <select
          className="select-field"
          value={data.type}
          onChange={(e) => setData({...data, type: e.target.value})}
          style={{fontSize: '13px', padding: '4px', width: '100%'}}
        >
          <option value="quantity">Quantity</option>
          <option value="sized">Sized</option>
        </select>
      </td>
      <td style={styles.td}>
        <select
          className="select-field"
          value={data.category}
          onChange={(e) => setData({...data, category: e.target.value})}
          style={{fontSize: '13px', padding: '4px', width: '100%'}}
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </td>
      <td style={styles.td}>
        <input
          className="input-field"
          value={data.options}
          onChange={(e) => setData({...data, options: e.target.value})}
          style={{fontSize: '13px', padding: '4px', width: '100%'}}
        />
      </td>
      <td style={styles.td}>
        <input
          type="text"
          className="input-field"
          value={data.price_per_unit}
          onChange={(e) => setData({...data, price_per_unit: e.target.value})}
          placeholder="530,750"
          style={{fontSize: '13px', padding: '4px', width: '100%'}}
        />
      </td>
      <td style={styles.td}>
        <input
          type="text"
          className="input-field"
          value={data.branch_price}
          onChange={(e) => setData({...data, branch_price: e.target.value})}
          placeholder="480,690"
          style={{fontSize: '13px', padding: '4px', width: '100%'}}
        />
      </td>
      <td style={styles.td}>
        <input
          className="input-field"
          value={data.unit}
          onChange={(e) => setData({...data, unit: e.target.value})}
          style={{fontSize: '13px', padding: '4px', width: '100%'}}
        />
      </td>
      <td style={styles.td}>
        <input
          type="checkbox"
          checked={data.is_kiosk}
          onChange={(e) => setData({...data, is_kiosk: e.target.checked})}
          style={{width: '18px', height: '18px', cursor: 'pointer'}}
        />
      </td>
      <td style={styles.td}>
        <input
          type="checkbox"
          checked={data.is_express}
          onChange={(e) => setData({...data, is_express: e.target.checked})}
          style={{width: '18px', height: '18px', cursor: 'pointer'}}
        />
      </td>
      <td style={styles.td}>
        <input
          type="checkbox"
          checked={data.only_branch}
          onChange={(e) => setData({...data, only_branch: e.target.checked})}
          style={{width: '18px', height: '18px', cursor: 'pointer'}}
        />
      </td>
      <td style={styles.td}>
        <select
          className="select-field"
          value={data.active}
          onChange={(e) => setData({...data, active: e.target.value === 'true'})}
          style={{fontSize: '14px', padding: '6px'}}
        >
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </td>
      <td style={styles.td}>
        <div style={styles.actions}>
          <button onClick={() => onSave(data)} style={styles.saveBtn} title="Save">
            <Save size={14} />
          </button>
          <button onClick={onCancel} style={styles.cancelBtn} title="Cancel">
            <X size={14} />
          </button>
        </div>
      </td>
    </>
  );
};

const styles = {
  container: {
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    color: '#6b4423',
    margin: 0,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: '#6b4423',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#8b5e34',
  },
  tableWrapper: {
    overflowX: 'hidden',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  th: {
    textAlign: 'left',
    padding: '12px 8px',
    background: '#f9f6f1',
    color: '#6b4423',
    fontWeight: '600',
    borderBottom: '2px solid #e8dcc8',
    fontSize: '13px',
    wordWrap: 'break-word',
  },
  tr: {
    borderBottom: '1px solid #e8dcc8',
  },
  td: {
    padding: '10px 8px',
    color: '#4a3728',
    fontSize: '13px',
    wordWrap: 'break-word',
    verticalAlign: 'top',
  },
  actions: {
    display: 'flex',
    gap: '4px',
    flexDirection: 'column',
  },
  editBtn: {
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteBtn: {
    background: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveBtn: {
    background: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelBtn: {
    background: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeBadge: {
    background: '#d1fae5',
    color: '#065f46',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  inactiveBadge: {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  yesBadge: {
    background: '#d1fae5',
    color: '#065f46',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  noBadge: {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  emptyState: {
    padding: '40px',
    textAlign: 'center',
    color: '#8b5e34',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '16px',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '16px',
  },
  hint: {
    fontSize: '12px',
    color: '#6b5d54',
    marginTop: '4px',
  },
  categoryBadge: {
    background: '#e0f2fe',
    color: '#075985',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  addCategoryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: '#6b4423',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  saveNewCategoryBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#16a34a',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px',
    cursor: 'pointer',
  },
  cancelNewCategoryBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '8px',
    cursor: 'pointer',
  },
  yesBadge: {
    background: '#d1fae5',
    color: '#065f46',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  noBadge: {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
};

export default RawMaterialsManager;
